import { useEffect, useState } from 'react'
import { ChevronRightIcon, CircleHelp, PencilIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import type {
  Frequency13849,
  PlrAssessment13849,
  PlrAssessmentOverride,
  PlrProbabilityOfOccurrence,
  PlrReviewReason,
  PlrReviewStatus,
  Possibility13849,
  SafetyFunctionPlrUndeterminedReason,
  SafetyFunctionWithArchitecture,
  Severity13849,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { hasPlrAssessmentOverride } from '@artem-lex/compliance-documents'
import { getPlrCircleLabel, PlrCircleBadge } from '../../components/PlrCircleBadge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { EditSafetyFunctionSelectFieldDialog } from './EditSafetyFunctionSelectFieldDialog'
import { EditSafetyFunctionTextFieldDialog } from './EditSafetyFunctionTextFieldDialog'
import type { SafetyFunctionFormData } from './safetyFunctionForm.schema'
import { EnIso13849RiskGraph } from './EnIso13849RiskGraph'
import { SafetyFunctionLinkedRiskReductionMeasuresSection } from './SafetyFunctionLinkedRiskReductionMeasuresSection'
import { hazardDetailLinkStateFromSafetyFunction } from '../hazards/hazardDetailNavigation'
import {
  getOptionalFieldDisplayValue,
  getSafetyFunctionSourceLabel,
  toSafetyFunctionFormData,
} from './safetyFunctionsUtils'

type PlrAssessmentOverrideUpdateInput = {
  plrOverrideSeverity?: Severity13849 | null
  plrOverrideFrequency?: Frequency13849 | null
  plrOverridePossibilityOfAvoidance?: Possibility13849 | null
}

type SafetyFunctionDetailInfoWidgetsProps = {
  safetyFunction: SafetyFunctionWithArchitecture
  isUpdating: boolean
  onUpdate: (data: SafetyFunctionFormData, onSuccess?: () => void) => void
  onUpdateOccurrence: (
    payload: {
      plrProbabilityOfOccurrence: PlrProbabilityOfOccurrence
      plrProbabilityOfOccurrenceJustification?: string | null
    },
    onSuccess?: () => void
  ) => void
  onUpdateOverride: (
    payload: PlrAssessmentOverrideUpdateInput,
    onSuccess?: () => void
  ) => void
  onResetOverride?: () => void
  isResettingOverride?: boolean
  onConfirmPlr?: () => void
  isConfirmingPlr?: boolean
}

type EditableTextField = Exclude<
  keyof SafetyFunctionFormData,
  'name' | 'plcal' | 'plrdet'
>

type EditableSelectField = 'plcal' | 'plrdet'

type TextFieldConfig = {
  field: EditableTextField
  labelKey: string
  multiline?: boolean
  className?: string
}

const IDENTITY_TEXT_FIELDS: TextFieldConfig[] = [
  {
    field: 'sfid',
    labelKey: 'safetyFunctionsPage.srsFields.sfid',
  },
  {
    field: 'sftype',
    labelKey: 'safetyFunctionsPage.srsFields.sftype',
  },
  {
    field: 'description',
    labelKey: 'safetyFunctionsPage.createDialog.description',
    multiline: true,
    className: 'sm:col-span-2',
  },
]

const BEHAVIOUR_TEXT_FIELDS: TextFieldConfig[] = [
  {
    field: 'triggerevent',
    labelKey: 'safetyFunctionsPage.srsFields.triggerevent',
  },
  {
    field: 'reaction',
    labelKey: 'safetyFunctionsPage.srsFields.reaction',
  },
  {
    field: 'safestate',
    labelKey: 'safetyFunctionsPage.srsFields.safestate',
  },
  {
    field: 'responsetime',
    labelKey: 'safetyFunctionsPage.srsFields.responsetime',
  },
  {
    field: 'opmode',
    labelKey: 'safetyFunctionsPage.srsFields.opmode',
  },
  {
    field: 'requestfrequency',
    labelKey: 'safetyFunctionsPage.srsFields.requestfrequency',
  },
]

const PERFORMANCE_TEXT_FIELDS: TextFieldConfig[] = [
  {
    field: 'sfinterfaces',
    labelKey: 'safetyFunctionsPage.srsFields.sfinterfaces',
    multiline: true,
    className: 'sm:col-span-2',
  },
  {
    field: 'priority',
    labelKey: 'safetyFunctionsPage.srsFields.priority',
  },
]

const ALL_TEXT_FIELDS: TextFieldConfig[] = [
  ...IDENTITY_TEXT_FIELDS,
  ...BEHAVIOUR_TEXT_FIELDS,
  ...PERFORMANCE_TEXT_FIELDS,
]

function getEnumDisplayValue(
  value: string | null | undefined,
  enumPrefix: 'plrdet',
  naLabel: string,
  translate: (key: string) => string
): string {
  if (value == null || value.trim() === '') {
    return naLabel
  }
  return translate(`safetyFunctionsPage.enums.${enumPrefix}.${value}`)
}

function getHazardTypeLabel(
  type: string | null,
  naLabel: string,
  translate: (key: string) => string
): string {
  if (!type) {
    return naLabel
  }
  const key = `hazardsPage.enums.hazardTypes.${type}`
  const translated = translate(key)
  return translated === key ? type : translated
}

function getPlr13849DimensionLabel(
  category: 'severity' | 'frequency' | 'possibility',
  value: string,
  translate: (key: string) => string
): string {
  return translate(`safetyFunctionsPage.sfDetail.plr13849.${category}.${value}`)
}

function getDeterminingHazardLabel(
  plrAssessment: PlrAssessment13849,
  naLabel: string,
  translate: (key: string) => string
): string {
  const hazardTypeLabel = getHazardTypeLabel(
    plrAssessment.determiningHazard.type,
    naLabel,
    translate
  )
  const description = plrAssessment.determiningHazard.description?.trim()
  if (description != null && description.length > 0) {
    return `${hazardTypeLabel} — ${description}`
  }
  return hazardTypeLabel
}

type Plr13849DimensionCategory = 'severity' | 'frequency' | 'possibility'

const PLR_SEVERITY_OPTIONS = ['S1', 'S2'] as const satisfies readonly Severity13849[]
const PLR_FREQUENCY_OPTIONS = ['F1', 'F2'] as const satisfies readonly Frequency13849[]
const PLR_POSSIBILITY_OPTIONS = ['P1', 'P2'] as const satisfies readonly Possibility13849[]

type PlrAssessmentDetailsProps = {
  plrAssessment: PlrAssessment13849
  plrInferredAssessment: PlrAssessment13849
  plrAssessmentOverride: PlrAssessmentOverride | null | undefined
  plr: string | null | undefined
  probabilityOfOccurrence: PlrProbabilityOfOccurrence
  safetyFunctionId: string
  naLabel: string
  translate: (key: string, options?: Record<string, string>) => string
  showPlrConfirmed?: boolean
  showConfirmPlrButton?: boolean
  onConfirmPlr?: () => void
  isConfirmingPlr?: boolean
  isUpdating?: boolean
  isResettingOverride?: boolean
  onUpdateOverride: (
    payload: PlrAssessmentOverrideUpdateInput,
    onSuccess?: () => void
  ) => void
  onResetOverride?: () => void
}

type PlrAssessmentDimensionFieldProps = {
  category: Plr13849DimensionCategory
  effectiveValue: string
  inferredValue: string
  isOverridden: boolean
  isUpdating: boolean
  translate: (key: string, options?: Record<string, string>) => string
  onUpdateOverride: PlrAssessmentDetailsProps['onUpdateOverride']
}

function getPlrAssessmentOverrideField(
  category: Plr13849DimensionCategory
): keyof PlrAssessmentOverrideUpdateInput {
  if (category === 'severity') {
    return 'plrOverrideSeverity'
  }
  if (category === 'frequency') {
    return 'plrOverrideFrequency'
  }
  return 'plrOverridePossibilityOfAvoidance'
}

function getPlrAssessmentDimensionOptions(category: Plr13849DimensionCategory) {
  if (category === 'severity') {
    return PLR_SEVERITY_OPTIONS
  }
  if (category === 'frequency') {
    return PLR_FREQUENCY_OPTIONS
  }
  return PLR_POSSIBILITY_OPTIONS
}

function getPlrAssessmentDimensionLabelKey(
  category: Plr13849DimensionCategory
): string {
  if (category === 'severity') {
    return 'safetyFunctionsPage.sfDetail.plrSeverityLabel'
  }
  if (category === 'frequency') {
    return 'safetyFunctionsPage.sfDetail.plrFrequencyLabel'
  }
  return 'safetyFunctionsPage.sfDetail.plrPossibilityLabel'
}

function PlrAssessmentDimensionField({
  category,
  effectiveValue,
  inferredValue,
  isOverridden,
  isUpdating,
  translate,
  onUpdateOverride,
}: PlrAssessmentDimensionFieldProps) {
  const options = getPlrAssessmentDimensionOptions(category)

  const handleChange = (value: string | null) => {
    if (value == null) {
      return
    }
    const overrideField = getPlrAssessmentOverrideField(category)
    onUpdateOverride({
      [overrideField]: value === inferredValue ? null : value,
    })
  }

  const showInferredHint = isOverridden && effectiveValue !== inferredValue

  return (
    <div className='space-y-1'>
      <Label className='text-sm font-medium text-foreground'>
        {translate(getPlrAssessmentDimensionLabelKey(category))}
      </Label>
      <Select
        value={effectiveValue}
        onValueChange={handleChange}
        disabled={isUpdating}
      >
        <SelectTrigger className='w-full max-w-md bg-background'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {getPlr13849DimensionLabel(category, option, translate)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showInferredHint && (
        <p className='text-xs text-muted-foreground'>
          {translate('safetyFunctionsPage.sfDetail.plr13849.inferredHint', {
            value: getPlr13849DimensionLabel(category, inferredValue, translate),
          })}
        </p>
      )}
    </div>
  )
}

function getOccurrenceLabel(
  occurrence: PlrProbabilityOfOccurrence,
  translate: (key: string) => string
): string {
  return occurrence === 'LOW'
    ? translate('safetyFunctionsPage.sfDetail.plrOccurrence.low')
    : translate('safetyFunctionsPage.sfDetail.plrOccurrence.high')
}

function PlrAssessmentDetails({
  plrAssessment,
  plrInferredAssessment,
  plrAssessmentOverride,
  plr,
  probabilityOfOccurrence,
  safetyFunctionId,
  naLabel,
  translate,
  showPlrConfirmed = false,
  showConfirmPlrButton = false,
  onConfirmPlr,
  isConfirmingPlr = false,
  isUpdating = false,
  isResettingOverride = false,
  onUpdateOverride,
  onResetOverride,
}: PlrAssessmentDetailsProps) {
  const determiningHazardLabel = getDeterminingHazardLabel(
    plrInferredAssessment,
    naLabel,
    translate
  )
  const showResetOverride =
    hasPlrAssessmentOverride(plrAssessmentOverride) && onResetOverride != null

  return (
    <div className='mt-2 min-w-0 space-y-3 text-sm text-muted-foreground'>
      <PlrAssessmentDimensionField
        category='severity'
        effectiveValue={plrAssessment.severity}
        inferredValue={plrInferredAssessment.severity}
        isOverridden={plrAssessmentOverride?.severity != null}
        isUpdating={isUpdating}
        translate={translate}
        onUpdateOverride={onUpdateOverride}
      />
      <PlrAssessmentDimensionField
        category='frequency'
        effectiveValue={plrAssessment.frequency}
        inferredValue={plrInferredAssessment.frequency}
        isOverridden={plrAssessmentOverride?.frequency != null}
        isUpdating={isUpdating}
        translate={translate}
        onUpdateOverride={onUpdateOverride}
      />
      <PlrAssessmentDimensionField
        category='possibility'
        effectiveValue={plrAssessment.possibilityOfAvoidance}
        inferredValue={plrInferredAssessment.possibilityOfAvoidance}
        isOverridden={plrAssessmentOverride?.possibilityOfAvoidance != null}
        isUpdating={isUpdating}
        translate={translate}
        onUpdateOverride={onUpdateOverride}
      />
      {showResetOverride && (
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={onResetOverride}
          disabled={isUpdating || isResettingOverride}
        >
          {translate(
            'safetyFunctionsPage.sfDetail.plr13849.recalculateFromHazards'
          )}
        </Button>
      )}
      {plr != null && plr.trim() !== '' && (
        <div className='flex flex-wrap items-center gap-x-2 gap-y-1 pt-1'>
          <span className='font-medium text-foreground'>
            {translate('safetyFunctionsPage.sfDetail.plrResultingLabel', {
              plrLevel: getPlrCircleLabel(plr).toLowerCase(),
              occurrenceLevel: getOccurrenceLabel(
                probabilityOfOccurrence,
                translate
              ),
            })}
          </span>
          {showPlrConfirmed && (
            <Badge variant='success' className='font-normal'>
              {translate('safetyFunctionsPage.plrReview.statusConfirmed')}
            </Badge>
          )}
          {showConfirmPlrButton && onConfirmPlr != null && (
            <Button
              type='button'
              size='sm'
              onClick={onConfirmPlr}
              disabled={isUpdating || isConfirmingPlr}
            >
              {translate('safetyFunctionsPage.plrReview.confirmButton')}
            </Button>
          )}
        </div>
      )}
      <div className='pt-1'>
        <dt className='font-medium text-foreground'>
          <span className='inline-flex items-center gap-1'>
            {translate('safetyFunctionsPage.sfDetail.plrDeterminingHazardLabel')}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  className='inline-flex text-muted-foreground/70 hover:text-muted-foreground'
                  aria-label={translate(
                    'safetyFunctionsPage.sfDetail.plrDeterminingHazardTooltip'
                  )}
                >
                  <CircleHelp className='size-3.5' aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-sm text-left'>
                {translate(
                  'safetyFunctionsPage.sfDetail.plrDeterminingHazardTooltip'
                )}
              </TooltipContent>
            </Tooltip>
          </span>
        </dt>
        <dd className='mt-0.5 flex items-start gap-1'>
          <span className='min-w-0 flex-1 break-words'>
            {determiningHazardLabel}
          </span>
          <Link
            relative='route'
            to={`../hazards/${plrInferredAssessment.determiningHazard.id}`}
            state={hazardDetailLinkStateFromSafetyFunction(safetyFunctionId)}
            className='inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground'
            aria-label={translate(
              'safetyFunctionsPage.sfDetail.viewDeterminingHazardAria'
            )}
          >
            <ChevronRightIcon className='size-4' />
          </Link>
        </dd>
      </div>
    </div>
  )
}

type PlrUndeterminedGuidanceProps = {
  reasons: SafetyFunctionPlrUndeterminedReason[]
  translate: (key: string) => string
}

function PlrUndeterminedGuidance({
  reasons,
  translate,
}: PlrUndeterminedGuidanceProps) {
  return (
    <Alert variant='info' className='mt-2'>
      <AlertTitle>
        {translate('safetyFunctionsPage.sfDetail.plrUndetermined.title')}
      </AlertTitle>
      <AlertDescription className='space-y-3'>
        <p>{translate('safetyFunctionsPage.sfDetail.plrUndetermined.guidance')}</p>
        <ul className='list-disc space-y-1 pl-5'>
          {reasons.length > 0 &&
            reasons.map((reason) => (
              <li key={reason}>
                {translate(
                  `safetyFunctionsPage.sfDetail.plrUndetermined.reasons.${reason}`
                )}
              </li>
            ))}
          {reasons.length === 0 && (
            <li>
              {translate(
                'safetyFunctionsPage.sfDetail.plrUndetermined.fallbackReason'
              )}
            </li>
          )}
        </ul>
        <Button variant='outline' size='sm' asChild>
          <Link relative='route' to='../risk-reduction-measures'>
            {translate(
              'safetyFunctionsPage.sfDetail.plrUndetermined.openRiskReductionMeasures'
            )}
          </Link>
        </Button>
      </AlertDescription>
    </Alert>
  )
}

type PlrProbabilityOfOccurrenceFieldProps = {
  occurrence: PlrProbabilityOfOccurrence
  justification: string | null
  hasPlrAssessment: boolean
  isUpdating: boolean
  translate: (key: string) => string
  onUpdateOccurrence: SafetyFunctionDetailInfoWidgetsProps['onUpdateOccurrence']
}

function PlrProbabilityOfOccurrenceField({
  occurrence: storedOccurrence,
  justification: storedJustification,
  hasPlrAssessment,
  isUpdating,
  translate,
  onUpdateOccurrence,
}: PlrProbabilityOfOccurrenceFieldProps) {
  const [occurrence, setOccurrence] =
    useState<PlrProbabilityOfOccurrence>(storedOccurrence)
  const [justificationDraft, setJustificationDraft] = useState(
    storedJustification ?? ''
  )
  const [justificationError, setJustificationError] = useState(false)

  useEffect(() => {
    setOccurrence(storedOccurrence)
    setJustificationDraft(storedJustification ?? '')
    setJustificationError(false)
  }, [storedOccurrence, storedJustification])

  const handleOccurrenceChange = (value: string) => {
    if (value !== 'HIGH' && value !== 'LOW') {
      return
    }

    if (value === 'LOW' && !hasPlrAssessment) {
      toast.error(
        translate(
          'safetyFunctionsPage.sfDetail.plrOccurrence.lowRequiresGraph'
        )
      )
      return
    }

    if (value === 'HIGH') {
      setOccurrence('HIGH')
      setJustificationDraft('')
      setJustificationError(false)
      onUpdateOccurrence({
        plrProbabilityOfOccurrence: 'HIGH',
        plrProbabilityOfOccurrenceJustification: null,
      })
      return
    }

    setOccurrence('LOW')
    setJustificationError(false)
  }

  const handleJustificationBlur = () => {
    if (occurrence !== 'LOW') {
      return
    }

    const trimmed = justificationDraft.trim()
    if (trimmed === '') {
      setJustificationError(true)
      return
    }

    setJustificationError(false)
    if (trimmed === (storedJustification ?? '').trim()) {
      return
    }

    onUpdateOccurrence({
      plrProbabilityOfOccurrence: 'LOW',
      plrProbabilityOfOccurrenceJustification: trimmed,
    })
  }

  return (
    <div className='mt-3 space-y-3'>
      <Alert
        variant='default'
        className='border-muted bg-muted/40 text-foreground'
      >
        <AlertDescription>
          {translate('safetyFunctionsPage.sfDetail.plrOccurrence.callout')}
        </AlertDescription>
      </Alert>

      <div className='space-y-2'>
        <Label className='text-sm font-medium'>
          {translate('safetyFunctionsPage.sfDetail.plrOccurrence.label')}
        </Label>
        <RadioGroup
          value={occurrence}
          onValueChange={handleOccurrenceChange}
          className='flex flex-wrap gap-4'
          disabled={isUpdating}
        >
          <div className='flex items-center space-x-2'>
            <RadioGroupItem
              value='HIGH'
              id='sf-plr-occurrence-high'
              disabled={isUpdating}
            />
            <Label
              htmlFor='sf-plr-occurrence-high'
              className='cursor-pointer font-normal'
            >
              {translate('safetyFunctionsPage.sfDetail.plrOccurrence.high')}
            </Label>
          </div>
          <div className='flex items-center space-x-2'>
            <RadioGroupItem
              value='LOW'
              id='sf-plr-occurrence-low'
              disabled={isUpdating || !hasPlrAssessment}
            />
            <Label
              htmlFor='sf-plr-occurrence-low'
              className='cursor-pointer font-normal'
            >
              {translate('safetyFunctionsPage.sfDetail.plrOccurrence.low')}
            </Label>
          </div>
        </RadioGroup>
      </div>

      {occurrence === 'LOW' && (
        <div className='space-y-2'>
          <Label htmlFor='sf-plr-occurrence-justification' className='text-sm'>
            {translate(
              'safetyFunctionsPage.sfDetail.plrOccurrence.justificationLabel'
            )}
          </Label>
          <Textarea
            id='sf-plr-occurrence-justification'
            value={justificationDraft}
            onChange={(event) => {
              setJustificationDraft(event.target.value)
              if (justificationError && event.target.value.trim() !== '') {
                setJustificationError(false)
              }
            }}
            onBlur={handleJustificationBlur}
            disabled={isUpdating}
            placeholder={translate(
              'safetyFunctionsPage.sfDetail.plrOccurrence.justificationPlaceholder'
            )}
            rows={3}
          />
          {justificationError && (
            <p className='text-sm text-destructive'>
              {translate(
                'safetyFunctionsPage.sfDetail.plrOccurrence.justificationRequired'
              )}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function getPlrReviewRequiredAlertMessage(
  plrReviewReason: PlrReviewReason | null | undefined,
  translate: (key: string) => string
): string {
  if (plrReviewReason === 'INFERRED_DRIFT_UNDER_OVERRIDE') {
    return translate(
      'safetyFunctionsPage.plrReview.reviewRequiredAlertInferredDrift'
    )
  }
  if (plrReviewReason === 'EFFECTIVE_ASSESSMENT_CHANGED') {
    return translate(
      'safetyFunctionsPage.plrReview.reviewRequiredAlertEffectiveChanged'
    )
  }
  return translate('safetyFunctionsPage.plrReview.reviewRequiredAlert')
}

export function SafetyFunctionDetailInfoWidgets({
  safetyFunction,
  isUpdating,
  onUpdate,
  onUpdateOccurrence,
  onUpdateOverride,
  onResetOverride,
  isResettingOverride = false,
  onConfirmPlr,
  isConfirmingPlr = false,
}: SafetyFunctionDetailInfoWidgetsProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [editingTextField, setEditingTextField] =
    useState<EditableTextField | null>(null)
  const [editingSelectField, setEditingSelectField] =
    useState<EditableSelectField | null>(null)

  const naLabel = t('hazardsPage.hazardDetail.na')
  const formData = toSafetyFunctionFormData(safetyFunction)
  const showEplanReference = safetyFunction.source === 'EPLAN'
  const plrAssessment = safetyFunction.plrAssessment ?? null
  const plrInferredAssessment =
    safetyFunction.plrInferredAssessment ?? plrAssessment
  const hasPlrAssessment = plrAssessment != null && plrInferredAssessment != null
  const plrReviewStatus: PlrReviewStatus | undefined =
    safetyFunction.plrReviewStatus
  const plrReviewReason = safetyFunction.plrReviewReason
  const showPlrRequiresReview = plrReviewStatus === 'REQUIRES_REVIEW'
  const showPlrConfirmed = plrReviewStatus === 'CONFIRMED'
  const plrUndeterminedReasons = safetyFunction.plrUndeterminedReasons ?? []
  const showPlrUndeterminedGuidance =
    safetyFunction.plr == null && !hasPlrAssessment
  const showPlrCircleBadge = hasPlrAssessment
  const showConfirmPlrButton =
    showPlrRequiresReview && onConfirmPlr != null
  let plrItemClassName: string | undefined
  if (hasPlrAssessment || showPlrUndeterminedGuidance) {
    plrItemClassName = 'sm:col-span-2'
  }

  const handleTextFieldSave = (data: SafetyFunctionFormData) => {
    onUpdate(data, () => {
      setEditingTextField(null)
    })
  }

  const handleSelectFieldSave = (data: SafetyFunctionFormData) => {
    onUpdate(data, () => {
      setEditingSelectField(null)
    })
  }

  const renderTextFieldItem = ({
    field,
    labelKey,
    multiline = false,
    className,
  }: TextFieldConfig) => {
    const fieldLabel = t(labelKey)
    const rawValue = formData[field]
    const displayValue = getOptionalFieldDisplayValue(rawValue, naLabel)

    return (
      <Item
        key={field}
        variant='outline'
        size={multiline ? 'xs' : undefined}
        className={className}
      >
        <ItemContent>
          <ItemTitle>{fieldLabel}</ItemTitle>
          <ItemDescription
            className={multiline ? 'whitespace-pre-wrap' : undefined}
          >
            {displayValue}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='size-8 shrink-0 text-muted-foreground'
            onClick={() => setEditingTextField(field)}
            disabled={isUpdating}
            aria-label={t('safetyFunctionsPage.sfDetail.editFieldAria', {
              field: fieldLabel,
            })}
          >
            <PencilIcon className='size-4' />
          </Button>
        </ItemActions>
      </Item>
    )
  }

  return (
    <>
      <div className='space-y-4'>
        <section className='space-y-2'>
          <h3 className='text-base font-semibold'>
            {t('safetyFunctionsPage.srsGroups.identity')}
          </h3>
          <div className='grid gap-2 sm:grid-cols-2'>
            <Item variant='outline'>
              <ItemContent>
                <ItemTitle>
                  {t('safetyFunctionsPage.sfDetail.sourceLabel')}
                </ItemTitle>
                <ItemDescription>
                  {getSafetyFunctionSourceLabel(safetyFunction.source, t)}
                </ItemDescription>
              </ItemContent>
            </Item>

            {showEplanReference && (
              <Item variant='outline'>
                <ItemContent>
                  <ItemTitle>
                    {t(
                      'safetyFunctionsPage.extractFromEplanModal.referenceIdLabel'
                    )}
                  </ItemTitle>
                  <ItemDescription>
                    {getOptionalFieldDisplayValue(
                      safetyFunction.eplanReferenceId,
                      naLabel
                    )}
                  </ItemDescription>
                </ItemContent>
              </Item>
            )}

            {IDENTITY_TEXT_FIELDS.map(renderTextFieldItem)}
          </div>
        </section>

        <section className='space-y-3'>
          <h3 className='text-base font-semibold'>
            {t('safetyFunctionsPage.srsGroups.behaviour')}
          </h3>
          <div className='grid gap-2 sm:grid-cols-3'>
            {BEHAVIOUR_TEXT_FIELDS.map(renderTextFieldItem)}
          </div>
        </section>

        <section className='space-y-3'>
          <h3 className='text-base font-semibold'>
            {t('safetyFunctionsPage.srsGroups.performance')}
          </h3>
          <div className='grid gap-2 sm:grid-cols-2'>
            {PERFORMANCE_TEXT_FIELDS.map(renderTextFieldItem)}

            <Item variant='outline' size='sm' className={plrItemClassName}>
              <ItemContent className='min-w-0'>
                <ItemTitle>{t('safetyFunctionsPage.srsFields.plr')}</ItemTitle>
                {hasPlrAssessment && (
                  <ItemDescription>
                    {t('safetyFunctionsPage.sfDetail.plrComputedHelper')}
                  </ItemDescription>
                )}
                {hasPlrAssessment && (
                  <PlrProbabilityOfOccurrenceField
                    occurrence={
                      safetyFunction.plrProbabilityOfOccurrence ?? 'HIGH'
                    }
                    justification={
                      safetyFunction.plrProbabilityOfOccurrenceJustification
                    }
                    hasPlrAssessment={hasPlrAssessment}
                    isUpdating={isUpdating}
                    translate={t}
                    onUpdateOccurrence={onUpdateOccurrence}
                  />
                )}
                {showPlrRequiresReview && (
                  <Alert variant='warning' className='mt-2'>
                    <AlertTitle>
                      {t('safetyFunctionsPage.plrReview.statusRequiresReview')}
                    </AlertTitle>
                    <AlertDescription>
                      {getPlrReviewRequiredAlertMessage(plrReviewReason, t)}
                    </AlertDescription>
                  </Alert>
                )}
                {showPlrUndeterminedGuidance && (
                  <PlrUndeterminedGuidance
                    reasons={plrUndeterminedReasons}
                    translate={t}
                  />
                )}
                {hasPlrAssessment && (
                  <>
                    <div className='mt-3'>
                      <EnIso13849RiskGraph
                        assessment={plrAssessment}
                        probabilityOfOccurrence={
                          safetyFunction.plrProbabilityOfOccurrence ?? 'HIGH'
                        }
                        highOccurrenceLabel={t(
                          'safetyFunctionsPage.sfDetail.plrOccurrence.high'
                        )}
                        lowOccurrenceLabel={t(
                          'safetyFunctionsPage.sfDetail.plrOccurrence.low'
                        )}
                        nodeTooltips={{
                          S1: t('safetyFunctionsPage.sfDetail.plrRiskGraphNodes.S1'),
                          S2: t('safetyFunctionsPage.sfDetail.plrRiskGraphNodes.S2'),
                          F1: t('safetyFunctionsPage.sfDetail.plrRiskGraphNodes.F1'),
                          F2: t('safetyFunctionsPage.sfDetail.plrRiskGraphNodes.F2'),
                          P1: t('safetyFunctionsPage.sfDetail.plrRiskGraphNodes.P1'),
                          P2: t('safetyFunctionsPage.sfDetail.plrRiskGraphNodes.P2'),
                        }}
                        ariaLabel={t(
                          'safetyFunctionsPage.sfDetail.plrRiskGraphAria'
                        )}
                      />
                    </div>
                    <PlrAssessmentDetails
                      plrAssessment={plrAssessment}
                      plrInferredAssessment={plrInferredAssessment}
                      plrAssessmentOverride={safetyFunction.plrAssessmentOverride}
                      plr={safetyFunction.plr}
                      probabilityOfOccurrence={
                        safetyFunction.plrProbabilityOfOccurrence ?? 'HIGH'
                      }
                      safetyFunctionId={safetyFunction.id}
                      naLabel={naLabel}
                      translate={t}
                      showPlrConfirmed={showPlrConfirmed}
                      showConfirmPlrButton={showConfirmPlrButton}
                      onConfirmPlr={onConfirmPlr}
                      isConfirmingPlr={isConfirmingPlr}
                      isUpdating={isUpdating}
                      isResettingOverride={isResettingOverride}
                      onUpdateOverride={onUpdateOverride}
                      onResetOverride={onResetOverride}
                    />
                  </>
                )}
              </ItemContent>
              {showPlrCircleBadge && (
                <ItemActions>
                  <PlrCircleBadge plr={safetyFunction.plr} />
                </ItemActions>
              )}
            </Item>

            <Item variant='outline' size='sm'>
              <ItemContent>
                <ItemTitle>{t('safetyFunctionsPage.srsFields.plcal')}</ItemTitle>
              </ItemContent>
              <ItemActions>
                <PlrCircleBadge plr={safetyFunction.plcal} />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => setEditingSelectField('plcal')}
                  disabled={isUpdating}
                  aria-label={t('safetyFunctionsPage.sfDetail.editFieldAria', {
                    field: t('safetyFunctionsPage.srsFields.plcal'),
                  })}
                >
                  <PencilIcon className='size-4' />
                </Button>
              </ItemActions>
            </Item>

            <Item variant='outline'>
              <ItemContent>
                <ItemTitle>
                  {t('safetyFunctionsPage.srsFields.plrdet')}
                </ItemTitle>
                <ItemDescription>
                  {getEnumDisplayValue(
                    safetyFunction.plrdet,
                    'plrdet',
                    naLabel,
                    t
                  )}
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => setEditingSelectField('plrdet')}
                  disabled={isUpdating}
                  aria-label={t('safetyFunctionsPage.sfDetail.editFieldAria', {
                    field: t('safetyFunctionsPage.srsFields.plrdet'),
                  })}
                >
                  <PencilIcon className='size-4' />
                </Button>
              </ItemActions>
            </Item>
          </div>
        </section>

        <SafetyFunctionLinkedRiskReductionMeasuresSection
          linkedRiskReductionMeasures={
            safetyFunction.linkedRiskReductionMeasures ?? []
          }
          referenceHazardId={
            safetyFunction.plrInferredAssessment?.determiningHazard.id ?? null
          }
          safetyFunctionId={safetyFunction.id}
        />
      </div>

      {editingTextField != null ? (
        <EditSafetyFunctionTextFieldDialog
          isOpen
          onClose={() => setEditingTextField(null)}
          safetyFunction={safetyFunction}
          field={editingTextField}
          labelKey={
            ALL_TEXT_FIELDS.find((config) => config.field === editingTextField)
              ?.labelKey ?? ''
          }
          multiline={
            ALL_TEXT_FIELDS.find((config) => config.field === editingTextField)
              ?.multiline
          }
          onSave={handleTextFieldSave}
          isSaving={isUpdating}
        />
      ) : null}

      {editingSelectField === 'plcal' ? (
        <EditSafetyFunctionSelectFieldDialog
          isOpen
          onClose={() => setEditingSelectField(null)}
          safetyFunction={safetyFunction}
          field='plcal'
          labelKey='safetyFunctionsPage.srsFields.plcal'
          onSave={handleSelectFieldSave}
          isSaving={isUpdating}
        />
      ) : null}

      {editingSelectField === 'plrdet' ? (
        <EditSafetyFunctionSelectFieldDialog
          isOpen
          onClose={() => setEditingSelectField(null)}
          safetyFunction={safetyFunction}
          field='plrdet'
          labelKey='safetyFunctionsPage.srsFields.plrdet'
          onSave={handleSelectFieldSave}
          isSaving={isUpdating}
        />
      ) : null}
    </>
  )
}
